/**
 * Firebase Cloud Messaging Adapter
 * Infrastructure layer - Implements push notification service using Firebase SDK
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { 
  getMessaging, 
  getToken, 
  onMessage, 
  isSupported,
  type Messaging,
  type MessagePayload
} from 'firebase/messaging';
import type { IPushNotificationService } from '../../domain/interfaces/PushNotificationService';
import type { PushNotificationPayload, PushSubscriptionData } from '../../domain/models/PushNotification';
import { getFirebaseConfig, type PushNotificationConfig } from '../../config/firebase.config';
import { createAxiosHttpClient } from '@/shared/features/query/infra/adapters/axios.adapter';

/**
 * Firebase Cloud Messaging Adapter
 * Implements IPushNotificationService using Firebase SDK
 */
export class FirebaseMessagingAdapter implements IPushNotificationService {
  private config: PushNotificationConfig;
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private initialized = false;
  private supported = false;

  constructor(config?: PushNotificationConfig) {
    this.config = config || getFirebaseConfig();
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return this.supported;
  }

  /**
   * Initialize Firebase and messaging
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (typeof window === 'undefined') {
      console.warn('Push notifications are not available in SSR');
      return;
    }

    if (!this.config.enabled) {
      console.warn('Push notifications are disabled');
      return;
    }

    try {
      // Check if messaging is supported in this browser
      this.supported = await isSupported();
      
      if (!this.supported) {
        console.warn('Firebase messaging is not supported in this browser');
        return;
      }

      // Initialize Firebase app (or get existing instance)
      if (getApps().length === 0) {
        this.app = initializeApp(this.config.firebase);
      } else {
        this.app = getApps()[0];
      }

      // Get messaging instance
      this.messaging = getMessaging(this.app);
      this.initialized = true;

      // Register service worker
      await this.registerServiceWorker();

    } catch (error) {
      console.error('Failed to initialize Firebase messaging:', error);
      this.supported = false;
    }
  }

  /**
   * Register the Firebase messaging service worker
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });
      console.log('Service worker registered:', registration.scope);
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermission(): Promise<string | null> {
    if (!this.initialized || !this.messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }

      // Get FCM token
      return await this.getToken();

    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return null;
    }
  }

  /**
   * Get the current FCM token
   */
  async getToken(): Promise<string | null> {
    if (!this.initialized || !this.messaging) {
      console.warn('Firebase messaging not initialized');
      return null;
    }

    if (this.getPermissionStatus() !== 'granted') {
      return null;
    }

    try {
      const currentToken = await getToken(this.messaging, {
        vapidKey: this.config.vapidKey,
      });

      if (currentToken) {
        return currentToken;
      }
      
      console.log('No registration token available');
      return null;

    } catch (error) {
      console.error('Failed to get FCM token:', error);
      return null;
    }
  }

  /**
   * Subscribe to incoming foreground messages
   */
  onMessage(callback: (payload: PushNotificationPayload) => void): () => void {
    if (!this.initialized || !this.messaging) {
      console.warn('Firebase messaging not initialized');
      return () => {};
    }

    const unsubscribe = onMessage(this.messaging, (payload: MessagePayload) => {
      const notification = payload.data || payload.notification;
      
      if (notification) {
        callback({
          title: (notification as Record<string, string>).title || '',
          body: (notification as Record<string, string>).body || '',
          icon: (notification as Record<string, string>).icon,
          image: (notification as Record<string, string>).image,
          clickAction: (notification as Record<string, string>).click_action,
        });
      }
    });

    return unsubscribe;
  }

  /**
   * Send subscription data to backend
   */
  async subscribeToBackend(data: PushSubscriptionData): Promise<void> {
    const httpClient = createAxiosHttpClient();

    await httpClient.request<void>({
      url: '/push-notification/subscribe',
      method: 'POST',
      data: {
        deviceToken: data.deviceToken,
        customerId: data.customerId || '',
        visitorId: data.visitorId || '',
        client: data.client,
      },
    });
  }
}

/**
 * Singleton instance
 */
let instance: FirebaseMessagingAdapter | null = null;

/**
 * Get or create Firebase messaging adapter instance
 */
export function getFirebaseMessagingAdapter(): FirebaseMessagingAdapter {
  if (!instance) {
    instance = new FirebaseMessagingAdapter();
  }
  return instance;
}
